/**
 * @fileoverview Supabase-backed persistence client for the web runtime.
 *
 * Parity-first design: the entire {@link PersistedState} is stored as a single
 * JSON document per user in the `user_state` table (mirroring the desktop
 * single-blob model). Row-level security guarantees a user can only read and
 * write their own row. A local cache provides offline/pre-auth continuity.
 *
 * A future migration may normalize this into relational tables (albums,
 * stickers, destinations); the JSON document is the migration source.
 */

import { getSupabase } from './supabase'
import { openExternalInBrowser } from './externalLink'
import { buildDefaultState, normalizeState } from '../../../shared/persistence'
import type { PersistedState } from '../../../shared/persistence'
import type { PersistenceClient } from './persistenceClient'
import type { SupabaseClient } from '@supabase/supabase-js'
import {
  CLOUD_ALBUMS_TABLE,
  CLOUD_DESTINATIONS_TABLE,
  CLOUD_PREFERENCES_TABLE,
  CLOUD_STICKERS_TABLE,
  LEGACY_STATE_TABLE,
  deserializePersistedStateFromNormalizedCloud,
  isEmptyNormalizedPayload,
  serializePersistedStateToNormalizedCloud
} from './cloudPersistenceMapping'

/** Local cache key used for offline and pre-authentication continuity. */
const CACHE_KEY = 'investalbum-cloud-cache-v1'

function readCache(): PersistedState {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    if (!raw) return buildDefaultState()
    return normalizeState(JSON.parse(raw) as Partial<PersistedState>)
  } catch {
    return buildDefaultState()
  }
}

function writeCache(state: PersistedState): void {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(normalizeState(state)))
  } catch {
    /* non-fatal */
  }
}

/**
 * Build a {@link PersistenceClient} backed by Supabase Postgres.
 * Must only be called when Supabase is configured.
 */
export function createCloudClient(): PersistenceClient {
  const maybeClient = getSupabase()
  if (!maybeClient) {
    throw new Error('createCloudClient called without Supabase configuration')
  }
  const client: SupabaseClient = maybeClient

  async function currentUserId(): Promise<string | null> {
    const { data } = await client.auth.getUser()
    return data.user?.id ?? null
  }

  async function loadLegacyState(userId: string): Promise<PersistedState> {
    const { data, error } = await client
      .from(LEGACY_STATE_TABLE)
      .select('state')
      .eq('user_id', userId)
      .maybeSingle()
    if (error) {
      throw new Error(error.message)
    }

    return data?.state ? normalizeState(data.state as Partial<PersistedState>) : buildDefaultState()
  }

  async function loadNormalizedState(userId: string): Promise<PersistedState> {
    const { data: albums, error: albumsError } = await client
      .from(CLOUD_ALBUMS_TABLE)
      .select(
        'id, user_id, name, size, theme_key, currency, target_finish_date, created_at, updated_at'
      )
      .eq('user_id', userId)
      .order('created_at', { ascending: true })

    if (albumsError) {
      throw new Error(albumsError.message)
    }

    const { data: preferences, error: preferencesError } = await client
      .from(CLOUD_PREFERENCES_TABLE)
      .select(
        'user_id, active_album_id, locale, theme_mode, album_page_size, legal_accepted, updated_at'
      )
      .eq('user_id', userId)
      .maybeSingle()

    if (preferencesError) {
      throw new Error(preferencesError.message)
    }

    if (isEmptyNormalizedPayload({ albums: albums ?? [], preferences: preferences ?? null })) {
      return buildDefaultState()
    }

    const albumIds = (albums ?? []).map((album) => album.id)
    const { data: destinations, error: destinationsError } = albumIds.length
      ? await client
          .from(CLOUD_DESTINATIONS_TABLE)
          .select('id, album_id, name, kind, notes, created_at, updated_at')
          .in('album_id', albumIds)
      : { data: [], error: null }

    if (destinationsError) {
      throw new Error(destinationsError.message)
    }

    const { data: stickerInvestments, error: stickerInvestmentsError } = albumIds.length
      ? await client
          .from(CLOUD_STICKERS_TABLE)
          .select('album_id, sticker_number, destination_id, comments, invested_at')
          .in('album_id', albumIds)
      : { data: [], error: null }

    if (stickerInvestmentsError) {
      throw new Error(stickerInvestmentsError.message)
    }

    return deserializePersistedStateFromNormalizedCloud({
      albums: albums ?? [],
      destinations: destinations ?? [],
      stickerInvestments: stickerInvestments ?? [],
      preferences: preferences ?? null
    })
  }

  async function saveLegacyState(userId: string, state: PersistedState): Promise<void> {
    const { error } = await client.from(LEGACY_STATE_TABLE).upsert(
      {
        user_id: userId,
        state: normalizeState(state),
        updated_at: new Date().toISOString()
      },
      { onConflict: 'user_id' }
    )
    if (error) {
      throw new Error(error.message)
    }
  }

  async function saveNormalizedState(userId: string, state: PersistedState): Promise<void> {
    const payload = serializePersistedStateToNormalizedCloud(state, userId)
    const albumIds = payload.albums.map((album) => album.id)

    const { data: existingAlbums, error: existingAlbumsError } = await client
      .from(CLOUD_ALBUMS_TABLE)
      .select('id')
      .eq('user_id', userId)

    if (existingAlbumsError) {
      throw new Error(existingAlbumsError.message)
    }

    const existingAlbumIds = (existingAlbums ?? []).map((album) => album.id)
    const removedAlbumIds = existingAlbumIds.filter((id) => !albumIds.includes(id))

    if (payload.albums.length > 0) {
      const { error: albumsUpsertError } = await client
        .from(CLOUD_ALBUMS_TABLE)
        .upsert(payload.albums, { onConflict: 'id' })
      if (albumsUpsertError) {
        throw new Error(albumsUpsertError.message)
      }
    }

    if (removedAlbumIds.length > 0) {
      const { error: deleteAlbumsError } = await client
        .from(CLOUD_ALBUMS_TABLE)
        .delete()
        .in('id', removedAlbumIds)
      if (deleteAlbumsError) {
        throw new Error(deleteAlbumsError.message)
      }
    }

    if (albumIds.length > 0) {
      const { error: deleteDestinationsError } = await client
        .from(CLOUD_DESTINATIONS_TABLE)
        .delete()
        .in('album_id', albumIds)
      if (deleteDestinationsError) {
        throw new Error(deleteDestinationsError.message)
      }

      const { error: deleteStickerInvestmentsError } = await client
        .from(CLOUD_STICKERS_TABLE)
        .delete()
        .in('album_id', albumIds)
      if (deleteStickerInvestmentsError) {
        throw new Error(deleteStickerInvestmentsError.message)
      }

      if (payload.destinations.length > 0) {
        const { error: insertDestinationsError } = await client
          .from(CLOUD_DESTINATIONS_TABLE)
          .insert(payload.destinations)
        if (insertDestinationsError) {
          throw new Error(insertDestinationsError.message)
        }
      }

      if (payload.stickerInvestments.length > 0) {
        const { error: insertStickerInvestmentsError } = await client
          .from(CLOUD_STICKERS_TABLE)
          .insert(payload.stickerInvestments)
        if (insertStickerInvestmentsError) {
          throw new Error(insertStickerInvestmentsError.message)
        }
      }
    }

    const { error: preferencesError } = await client
      .from(CLOUD_PREFERENCES_TABLE)
      .upsert(payload.preferences, { onConflict: 'user_id' })

    if (preferencesError) {
      throw new Error(preferencesError.message)
    }
  }

  return {
    platform: 'web-cloud',
    isCloud: true,

    async loadState(): Promise<PersistedState> {
      const userId = await currentUserId()
      if (!userId) return readCache()

      try {
        const normalizedState = await loadNormalizedState(userId)
        if (normalizedState.albums.length > 0 || normalizedState.activeAlbumId !== null) {
          writeCache(normalizedState)
          return normalizedState
        }
      } catch (error) {
        console.warn('[InvestAlbum] Normalized cloud load unavailable, trying legacy state:', error)
      }

      try {
        const legacyState = await loadLegacyState(userId)
        writeCache(legacyState)

        if (legacyState.albums.length > 0) {
          try {
            await saveNormalizedState(userId, legacyState)
          } catch (migrationError) {
            console.warn('[InvestAlbum] Failed to backfill normalized cloud state:', migrationError)
          }
        }

        return legacyState
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        console.error('[InvestAlbum] Cloud load failed, using local cache:', message)
        return readCache()
      }
    },

    async saveState(state: PersistedState): Promise<void> {
      writeCache(state)
      const userId = await currentUserId()
      if (!userId) return

      try {
        await saveNormalizedState(userId, state)
        return
      } catch (error) {
        console.warn(
          '[InvestAlbum] Normalized cloud save unavailable, falling back to legacy blob:',
          error
        )
      }

      await saveLegacyState(userId, state)
    },

    saveStateSync(state: PersistedState): void {
      // Network writes cannot block `beforeunload`; cache locally and let the
      // next async save reconcile to the cloud.
      writeCache(state)
    },

    openExternalLink: openExternalInBrowser
  }
}
